import { inject, type Provider } from '@angular/core';
import { Store } from '@ngrx/store';

import {
  EditorWorkflowNavigation,
  ENGINE_STATE_EXTENSION,
  type EngineStateExtension,
  LayoutHistory,
  WorkflowStepDebugger,
} from '@blaze-runtime/engine-data-access';

import { getIsRestoring, historyReducerTypes, initialHistorySlice } from './history';
import {
  findAndOpenWorkflow,
  initialTargetWorkflowSlice,
  targetWorkflowReducerTypes,
} from './target-workflow';
import {
  debugMonitor,
  initialWorkflowDebuggerSlice,
  workflowDebuggerReducerTypes,
} from './workflow-debugger';

/**
 * The editor's additions to the `engine` feature slice, in the order they must be applied.
 *
 * The debugger reacts to two workflow actions the engine's own runtime reducers also handle
 * (`applyWorkflow`, `connectionStepComplete`) and must see their result, which is why extensions
 * run after every engine handler — see `EngineStateExtension`.
 */
const EDITOR_ENGINE_STATE_EXTENSIONS: readonly EngineStateExtension[] = [
  { initialState: initialWorkflowDebuggerSlice, reducerTypes: workflowDebuggerReducerTypes },
  { initialState: initialTargetWorkflowSlice, reducerTypes: targetWorkflowReducerTypes },
  { initialState: initialHistorySlice, reducerTypes: historyReducerTypes },
];

/**
 * Adds the editor's authoring state — undo/redo history, workflow targeting, the workflow
 * debugger — to the `engine` feature slice, and binds the ports the engine uses to consult it.
 *
 * Register it in the **root** injector, next to `EngineModule`: `StoreModule.forFeature` builds the
 * `engine` reducer at bootstrap, so extensions provided from a lazily-loaded injector arrive too
 * late to be included. A runtime that omits this provider runs on the engine's own slices alone.
 */
export function provideEditorEngineState(): Provider[] {
  return [
    ...EDITOR_ENGINE_STATE_EXTENSIONS.map((extension) => ({
      provide: ENGINE_STATE_EXTENSION,
      multi: true,
      useValue: extension,
    })),
    {
      provide: LayoutHistory,
      useFactory: (): LayoutHistory => ({
        isRestoring$: inject(Store).select(getIsRestoring),
      }),
    },
    {
      provide: EditorWorkflowNavigation,
      useFactory: (): EditorWorkflowNavigation => {
        const store = inject(Store);
        return {
          revealWorkflowNode: (workflowId, nodeId) =>
            store.dispatch(findAndOpenWorkflow({ workflowId, nodeId, redirect: false })),
        };
      },
    },
    {
      provide: WorkflowStepDebugger,
      useFactory: (): WorkflowStepDebugger => {
        const store = inject(Store);
        return { interceptStep: () => debugMonitor(store) };
      },
    },
  ];
}
